import { MattersGeneralSearch } from "@/components/features/matters/MattersGeneralSearch";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { AddOrEditMatterModal } from "@/components/features/matter/layout/AddOrEditMatterModal/AddOrEditMatterModal";
import MattersSearchBar from "@/components/features/matters/MattersSearchBar/MattersSearchBar";
import { MattersTable } from "@/components/features/matters/MattersTable/MattersTable";
import { AppModal, Button, Typography } from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useSearchObject } from "@/hooks/useSearchObject";
import { type MatterSearchResult } from "@/lib/data/requests";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { useCreateMatterRelatedMatterConnection } from "@/lib/data/react-query/matters/matter-related-matter";
import { MATTER_STATUS_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { APIResponse, Matter } from "@/lib/types";
import { useQuery } from "@tanstack/react-query";
import { PlusCircle, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

const RELATED_MATTER_STATUS_CATEGORIES = MATTER_STATUS_CATEGORY_OPTIONS.filter(
  (category) => category !== "closed" && category !== "declined",
);

interface AddRelatedMatterModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  matterId: string;
}

export const AddRelatedMatterModal = ({
  open,
  setOpen,
  matterId,
}: AddRelatedMatterModalProps) => {
  const {
    data: matterResponse,
    isPending: isMatterPending,
    isError: isMatterError,
  } = useQuery({
    ...retrieveMatterQueryOptions(matterId),
    enabled: open && Boolean(matterId),
  });
  const matter = matterResponse?.data;
  const {
    mutateAsync: createRelatedMatterConnectionAsync,
    isPending: isCreatingRelatedMatter,
  } = useCreateMatterRelatedMatterConnection();

  const [selectedMatters, setSelectedMatters] = useState<MatterSearchResult[]>(
    [],
  );
  const [isCreatingMatter, setIsCreatingMatter] = useState(false);
  const [matterSearch, setMatterSearch] = useState("");
  const debouncedMatterSearch = useDebouncedValue(matterSearch, 250);

  const excludedMatterIds = useMemo(() => {
    const ids = new Set<string>([matterId]);
    matter?.relatedMatterConnections.forEach((connection) => {
      ids.add(connection.relatedMatterId);
    });
    return ids;
  }, [matter?.relatedMatterConnections, matterId]);

  const selectedMatterIds = useMemo(
    () =>
      new Set<string>(
        selectedMatters.map((selectedMatter) => selectedMatter.id),
      ),
    [selectedMatters],
  );

  useEffect(() => {
    if (!open) {
      setMatterSearch("");
      setSelectedMatters([]);
      setIsCreatingMatter(false);
    }
  }, [open]);

  const matterCreationDefaults = useMemo(() => {
    if (!matter) {
      return undefined;
    }
    const baseDefaults = {
      statusId: matter.statusId || "",
      incidentTypeId: matter.incidentTypeId || "",
      dateOfLoss: matter.dateOfLoss || "",
      incidentTime: matter.incidentTime ?? "",
      incidentDescription: matter.incidentDescription ?? "",
      criticalInfo: matter.criticalInfo ?? "",
      incidentAddressStreet1: matter.incidentAddressStreet1 ?? "",
      incidentAddressStreet2: matter.incidentAddressStreet2 ?? "",
      incidentAddressCity: matter.incidentAddressCity ?? "",
      incidentAddressCounty: matter.incidentAddressCounty ?? "",
      incidentAddressState: matter.incidentAddressState || "",
      incidentAddressCountry: matter.incidentAddressCountry || "United States",
      incidentAddressZip: matter.incidentAddressZip ?? "",
      incidentAddressDetails: matter.incidentAddressDetails ?? "",
    };

    const attorneyFeeOverride =
      typeof matter.attorneyFee === "number" &&
      !Number.isNaN(matter.attorneyFee)
        ? { matterAttorneyFee: matter.attorneyFee }
        : {};
    return {
      ...baseDefaults,
      ...attorneyFeeOverride,
    };
  }, [matter]);

  const handleAfterCreateMatter = useCallback((result: APIResponse<Matter>) => {
    const createdMatter = result.data;
    const createdMatterId = createdMatter.id;
    if (!createdMatterId) {
      setIsCreatingMatter(false);
      return;
    }
    setSelectedMatters((prev) => {
      const alreadySelected = prev.some(
        (matter) => matter.id === createdMatterId,
      );
      if (alreadySelected) {
        return prev;
      }
      return [
        ...prev,
        {
          id: createdMatterId,
          dateCreated: createdMatter.dateCreated,
          dateModified: createdMatter.dateModified,
          title: createdMatter.title,
          statusId: createdMatter.statusId,
          incidentTypeId: createdMatter.incidentTypeId,
          dateOfLoss: createdMatter.dateOfLoss,
          incidentAddressCity: createdMatter.incidentAddressCity,
          incidentAddressState: createdMatter.incidentAddressState,
          incidentAddressStreet1: createdMatter.incidentAddressStreet1,
          incidentAddressStreet2: createdMatter.incidentAddressStreet2,
          incidentAddressZip: createdMatter.incidentAddressZip,
          incidentAddressCountry: createdMatter.incidentAddressCountry,
        },
      ];
    });
    setIsCreatingMatter(false);
  }, []);

  const {
    searchObject,
    addSearchObjectValue,
    removeSearchObjectValue,
    clearSearchObject,
  } = useSearchObject({
    defaultSearchObject: {},
    itemType: "matter",
  });

  const searchExclusions = useMemo(
    () => [...new Set([...excludedMatterIds, ...selectedMatterIds])],
    [excludedMatterIds, selectedMatterIds],
  );

  const toggleMatterSelection = useCallback((matter: MatterSearchResult) => {
    setSelectedMatters((prev) => {
      const alreadySelected = prev.some(
        (selectedMatter) => selectedMatter.id === matter.id,
      );
      if (alreadySelected) {
        return prev.filter((selectedMatter) => selectedMatter.id !== matter.id);
      }
      return [...prev, matter];
    });
  }, []);

  const handleRemoveSelectedMatter = useCallback((matterIdToRemove: string) => {
    setSelectedMatters((prev) =>
      prev.filter((matter) => matter.id !== matterIdToRemove),
    );
  }, []);

  const handleBatchRelate = useCallback(async () => {
    if (selectedMatters.length === 0) {
      return;
    }
    for (const selectedMatter of selectedMatters) {
      const selectedMatterId = selectedMatter.id;
      if (!selectedMatterId) {
        continue;
      }
      await createRelatedMatterConnectionAsync({
        matterId,
        relatedMatterId: selectedMatterId,
      });
    }
    setSelectedMatters([]);
    setOpen(false);
  }, [createRelatedMatterConnectionAsync, matterId, selectedMatters, setOpen]);

  return (
    <>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        title="Add related matter"
        size="4xl"
        className="h-[min(85vh,820px)] w-[calc(100vw-2rem)]"
        bodyClassName="min-h-0 px-0"
        bodyContained
        loading={isCreatingRelatedMatter || (open && isMatterPending)}
        footer={
          <div className="flex w-full flex-wrap justify-end gap-2">
            <FormCancelButton setOpen={setOpen} />
            <Button
              type="button"
              onClick={handleBatchRelate}
              disabled={isCreatingRelatedMatter || selectedMatters.length === 0}
            >
              Save &amp; Close
            </Button>
          </div>
        }
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-8">
          {selectedMatters.length > 0 && (
            <div className="border-border shrink-0 border-b py-4">
              <div className="space-y-2">
                <Typography className="text-sm font-semibold">
                  Selected matters
                </Typography>
                <div className="flex flex-wrap gap-2">
                  {selectedMatters.map((selectedMatter) => {
                    const matterTitle = selectedMatter.title;
                    return (
                      <div
                        key={selectedMatter.id}
                        className="border-border flex items-center gap-2 rounded-full border px-3 py-1 text-sm"
                      >
                        <span className="max-w-[200px] truncate">
                          {matterTitle}
                        </span>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => {
                            handleRemoveSelectedMatter(selectedMatter.id);
                          }}
                        >
                          <X className="h-3 w-3" />
                          <span className="sr-only">
                            Remove {matterTitle} from selection
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden pt-4 pb-2">
            <div className="min-h-0 flex-1 overflow-y-auto px-1">
              {isMatterError && !matter && (
                <Typography>
                  Unable to load this matter. Please try again.
                </Typography>
              )}
              {matter && (
                <MattersTable
                  key={matterId}
                  showRelatedMatters
                  massTortId={matter.massTortId ?? undefined}
                  generalSearch={debouncedMatterSearch}
                  searchBar={
                    <MattersGeneralSearch
                      className="max-w-full"
                      value={matterSearch}
                      onChange={setMatterSearch}
                    />
                  }
                  toolbarFilters={
                    <MattersSearchBar
                      layout="bar"
                      clearSearchObject={clearSearchObject}
                      statusCategories={RELATED_MATTER_STATUS_CATEGORIES}
                      searchObject={searchObject}
                      addSearchObjectValue={addSearchObjectValue}
                      removeSearchObjectValue={removeSearchObjectValue}
                    />
                  }
                  statusCategories={RELATED_MATTER_STATUS_CATEGORIES}
                  tableName={tanstackTableNames.mattersSlideover}
                  searchObject={searchObject}
                  excludeMatterIds={searchExclusions}
                  onRowClick={(row) => {
                    if (isCreatingRelatedMatter) {
                      return;
                    }
                    toggleMatterSelection(row.original);
                  }}
                  tableButtons={
                    <Button
                      type="button"
                      variant="outline"
                      size="md"
                      onClick={() => {
                        setIsCreatingMatter(true);
                      }}
                    >
                      <PlusCircle className="h-4 w-4" />
                      <span className="pl-2">Create matter</span>
                    </Button>
                  }
                />
              )}
            </div>
          </div>
        </div>
      </AppModal>
      <AddOrEditMatterModal
        open={isCreatingMatter}
        setOpen={setIsCreatingMatter}
        editing={false}
        massTortId={matter?.massTortId ?? undefined}
        allowedMatterStatusCategories={RELATED_MATTER_STATUS_CATEGORIES}
        initialFormValues={matterCreationDefaults}
        afterCreateFn={handleAfterCreateMatter}
      />
    </>
  );
};
